import React from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkGFM from 'remark-gfm'    
import remarkMath from 'remark-math'
import remarkBreaks from 'remark-breaks'
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css' // `rehype-katex` does not import the CSS for you
import {micromark} from 'micromark'
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import { preprocessLaTeX } from './MarkdownLatex'

export { preprocessLaTeX } from './MarkdownLatex'

// Conversation and stored-tool-result links are application-internal opaque
// navigation handles. Preserve only those two custom schemes; every ordinary
// URL still goes through react-markdown's default safety transform.
const attunewiseUrlTransform = value => {
  const url = String(value || '')
  if (url.startsWith('conversation://') || url.startsWith('tool-result://')) return url
  return defaultUrlTransform(url)
}

const katexCss = `
@font-face{font-family:KaTeX_AMS;font-style:normal;font-weight:400;src:url(fonts/KaTeX_AMS-Regular.woff2) format("woff2"),url(fonts/KaTeX_AMS-Regular.woff) format("woff"),url(fonts/KaTeX_AMS-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Caligraphic;font-style:normal;font-weight:700;src:url(fonts/KaTeX_Caligraphic-Bold.woff2) format("woff2"),url(fonts/KaTeX_Caligraphic-Bold.woff) format("woff"),url(fonts/KaTeX_Caligraphic-Bold.ttf) format("truetype")}@font-face{font-family:KaTeX_Caligraphic;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Caligraphic-Regular.woff2) format("woff2"),url(fonts/KaTeX_Caligraphic-Regular.woff) format("woff"),url(fonts/KaTeX_Caligraphic-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Fraktur;font-style:normal;font-weight:700;src:url(fonts/KaTeX_Fraktur-Bold.woff2) format("woff2"),url(fonts/KaTeX_Fraktur-Bold.woff) format("woff"),url(fonts/KaTeX_Fraktur-Bold.ttf) format("truetype")}@font-face{font-family:KaTeX_Fraktur;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Fraktur-Regular.woff2) format("woff2"),url(fonts/KaTeX_Fraktur-Regular.woff) format("woff"),url(fonts/KaTeX_Fraktur-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Main;font-style:normal;font-weight:700;src:url(fonts/KaTeX_Main-Bold.woff2) format("woff2"),url(fonts/KaTeX_Main-Bold.woff) format("woff"),url(fonts/KaTeX_Main-Bold.ttf) format("truetype")}@font-face{font-family:KaTeX_Main;font-style:italic;font-weight:700;src:url(fonts/KaTeX_Main-BoldItalic.woff2) format("woff2"),url(fonts/KaTeX_Main-BoldItalic.woff) format("woff"),url(fonts/KaTeX_Main-BoldItalic.ttf) format("truetype")}@font-face{font-family:KaTeX_Main;font-style:italic;font-weight:400;src:url(fonts/KaTeX_Main-Italic.woff2) format("woff2"),url(fonts/KaTeX_Main-Italic.woff) format("woff"),url(fonts/KaTeX_Main-Italic.ttf) format("truetype")}@font-face{font-family:KaTeX_Main;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Main-Regular.woff2) format("woff2"),url(fonts/KaTeX_Main-Regular.woff) format("woff"),url(fonts/KaTeX_Main-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Math;font-style:italic;font-weight:700;src:url(fonts/KaTeX_Math-BoldItalic.woff2) format("woff2"),url(fonts/KaTeX_Math-BoldItalic.woff) format("woff"),url(fonts/KaTeX_Math-BoldItalic.ttf) format("truetype")}@font-face{font-family:KaTeX_Math;font-style:italic;font-weight:400;src:url(fonts/KaTeX_Math-Italic.woff2) format("woff2"),url(fonts/KaTeX_Math-Italic.woff) format("woff"),url(fonts/KaTeX_Math-Italic.ttf) format("truetype")}@font-face{font-family:"KaTeX_SansSerif";font-style:normal;font-weight:700;src:url(fonts/KaTeX_SansSerif-Bold.woff2) format("woff2"),url(fonts/KaTeX_SansSerif-Bold.woff) format("woff"),url(fonts/KaTeX_SansSerif-Bold.ttf) format("truetype")}@font-face{font-family:"KaTeX_SansSerif";font-style:italic;font-weight:400;src:url(fonts/KaTeX_SansSerif-Italic.woff2) format("woff2"),url(fonts/KaTeX_SansSerif-Italic.woff) format("woff"),url(fonts/KaTeX_SansSerif-Italic.ttf) format("truetype")}@font-face{font-family:"KaTeX_SansSerif";font-style:normal;font-weight:400;src:url(fonts/KaTeX_SansSerif-Regular.woff2) format("woff2"),url(fonts/KaTeX_SansSerif-Regular.woff) format("woff"),url(fonts/KaTeX_SansSerif-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Script;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Script-Regular.woff2) format("woff2"),url(fonts/KaTeX_Script-Regular.woff) format("woff"),url(fonts/KaTeX_Script-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Size1;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Size1-Regular.woff2) format("woff2"),url(fonts/KaTeX_Size1-Regular.woff) format("woff"),url(fonts/KaTeX_Size1-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Size2;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Size2-Regular.woff2) format("woff2"),url(fonts/KaTeX_Size2-Regular.woff) format("woff"),url(fonts/KaTeX_Size2-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Size3;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Size3-Regular.woff2) format("woff2"),url(fonts/KaTeX_Size3-Regular.woff) format("woff"),url(fonts/KaTeX_Size3-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Size4;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Size4-Regular.woff2) format("woff2"),url(fonts/KaTeX_Size4-Regular.woff) format("woff"),url(fonts/KaTeX_Size4-Regular.ttf) format("truetype")}@font-face{font-family:KaTeX_Typewriter;font-style:normal;font-weight:400;src:url(fonts/KaTeX_Typewriter-Regular.woff2) format("woff2"),url(fonts/KaTeX_Typewriter-Regular.woff) format("woff"),url(fonts/KaTeX_Typewriter-Regular.ttf) format("truetype")}.katex{font:normal 1.21em KaTeX_Main,Times New Roman,serif;line-height:1.2;text-indent:0;text-rendering:auto}.katex *{-ms-high-contrast-adjust:none!important;border-color:currentColor}.katex .katex-version:after{content:"0.16.10"}.katex .katex-mathml{clip:rect(1px,1px,1px,1px);border:0;height:1px;overflow:hidden;padding:0;position:absolute;width:1px}.katex .katex-html>.newline{display:block}.katex .base{position:relative;white-space:nowrap;width:-webkit-min-content;width:-moz-min-content;width:min-content}.katex .base,.katex .strut{display:inline-block}.katex .textbf{font-weight:700}.katex .textit{font-style:italic}.katex .textrm{font-family:KaTeX_Main}.katex .textsf{font-family:KaTeX_SansSerif}.katex .texttt{font-family:KaTeX_Typewriter}.katex .mathnormal{font-family:KaTeX_Math;font-style:italic}.katex .mathit{font-family:KaTeX_Main;font-style:italic}.katex .mathrm{font-style:normal}.katex .mathbf{font-family:KaTeX_Main;font-weight:700}.katex .boldsymbol{font-family:KaTeX_Math;font-style:italic;font-weight:700}.katex .amsrm,.katex .mathbb,.katex .textbb{font-family:KaTeX_AMS}.katex .mathcal{font-family:KaTeX_Caligraphic}.katex .mathfrak,.katex .textfrak{font-family:KaTeX_Fraktur}.katex .mathboldfrak,.katex .textboldfrak{font-family:KaTeX_Fraktur;font-weight:700}.katex .mathtt{font-family:KaTeX_Typewriter}.katex .mathscr,.katex .textscr{font-family:KaTeX_Script}.katex .mathsf,.katex .textsf{font-family:KaTeX_SansSerif}.katex .mathboldsf,.katex .textboldsf{font-family:KaTeX_SansSerif;font-weight:700}.katex .mathitsf,.katex .textitsf{font-family:KaTeX_SansSerif;font-style:italic}.katex .mainrm{font-family:KaTeX_Main;font-style:normal}.katex .vlist-t{border-collapse:collapse;display:inline-table;table-layout:fixed}.katex .vlist-r{display:table-row}.katex .vlist{display:table-cell;position:relative;vertical-align:bottom}.katex .vlist>span{display:block;height:0;position:relative}.katex .vlist>span>span{display:inline-block}.katex .vlist>span>.pstrut{overflow:hidden;width:0}.katex .vlist-t2{margin-right:-2px}.katex .vlist-s{display:table-cell;font-size:1px;min-width:2px;vertical-align:bottom;width:2px}.katex .vbox{align-items:baseline;display:inline-flex;flex-direction:column}.katex .hbox{width:100%}.katex .hbox,.katex .thinbox{display:inline-flex;flex-direction:row}.katex .thinbox{max-width:0;width:0}.katex .msupsub{text-align:left}.katex .mfrac>span>span{text-align:center}.katex .mfrac .frac-line{border-bottom-style:solid;display:inline-block;width:100%}.katex .hdashline,.katex .hline,.katex .mfrac .frac-line,.katex .overline .overline-line,.katex .rule,.katex .underline .underline-line{min-height:1px}.katex .mspace{display:inline-block}.katex .clap,.katex .llap,.katex .rlap{position:relative;width:0}.katex .clap>.inner,.katex .llap>.inner,.katex .rlap>.inner{position:absolute}.katex .clap>.fix,.katex .llap>.fix,.katex .rlap>.fix{display:inline-block}.katex .llap>.inner{right:0}.katex .clap>.inner,.katex .rlap>.inner{left:0}.katex .clap>.inner>span{margin-left:-50%;margin-right:50%}.katex .rule{border:0 solid;display:inline-block;position:relative}.katex .hline,.katex .overline .overline-line,.katex .underline .underline-line{border-bottom-style:solid;display:inline-block;width:100%}.katex .hdashline{border-bottom-style:dashed;display:inline-block;width:100%}.katex .sqrt>.root{margin-left:.27777778em;margin-right:-.55555556em}.katex .fontsize-ensurer.reset-size1.size1,.katex .sizing.reset-size1.size1{font-size:1em}.katex .fontsize-ensurer.reset-size1.size2,.katex .sizing.reset-size1.size2{font-size:1.2em}.katex .fontsize-ensurer.reset-size1.size3,.katex .sizing.reset-size1.size3{font-size:1.4em}.katex .fontsize-ensurer.reset-size1.size4,.katex .sizing.reset-size1.size4{font-size:1.6em}.katex .fontsize-ensurer.reset-size1.size5,.katex .sizing.reset-size1.size5{font-size:1.8em}.katex .fontsize-ensurer.reset-size1.size6,.katex .sizing.reset-size1.size6{font-size:2em}.katex .fontsize-ensurer.reset-size1.size7,.katex .sizing.reset-size1.size7{font-size:2.4em}.katex .fontsize-ensurer.reset-size1.size8,.katex .sizing.reset-size1.size8{font-size:2.88em}.katex .fontsize-ensurer.reset-size1.size9,.katex .sizing.reset-size1.size9{font-size:3.456em}.katex .fontsize-ensurer.reset-size1.size10,.katex .sizing.reset-size1.size10{font-size:4.148em}.katex .fontsize-ensurer.reset-size1.size11,.katex .sizing.reset-size1.size11{font-size:4.976em}.katex .fontsize-ensurer.reset-size2.size1,.katex .sizing.reset-size2.size1{font-size:.83333333em}.katex .fontsize-ensurer.reset-size2.size2,.katex .sizing.reset-size2.size2{font-size:1em}.katex .fontsize-ensurer.reset-size2.size3,.katex .sizing.reset-size2.size3{font-size:1.16666667em}.katex .fontsize-ensurer.reset-size2.size4,.katex .sizing.reset-size2.size4{font-size:1.33333333em}.katex .fontsize-ensurer.reset-size2.size5,.katex .sizing.reset-size2.size5{font-size:1.5em}.katex .fontsize-ensurer.reset-size2.size6,.katex .sizing.reset-size2.size6{font-size:1.66666667em}.katex .fontsize-ensurer.reset-size2.size7,.katex .sizing.reset-size2.size7{font-size:2em}.katex .fontsize-ensurer.reset-size2.size8,.katex .sizing.reset-size2.size8{font-size:2.4em}.katex .fontsize-ensurer.reset-size2.size9,.katex .sizing.reset-size2.size9{font-size:2.88em}.katex .fontsize-ensurer.reset-size2.size10,.katex .sizing.reset-size2.size10{font-size:3.45666667em}.katex .fontsize-ensurer.reset-size2.size11,.katex .sizing.reset-size2.size11{font-size:4.14666667em}.katex .fontsize-ensurer.reset-size3.size1,.katex .sizing.reset-size3.size1{font-size:.71428571em}.katex .fontsize-ensurer.reset-size3.size2,.katex .sizing.reset-size3.size2{font-size:.85714286em}.katex .fontsize-ensurer.reset-size3.size3,.katex .sizing.reset-size3.size3{font-size:1em}.katex .fontsize-ensurer.reset-size3.size4,.katex .sizing.reset-size3.size4{font-size:1.14285714em}.katex .fontsize-ensurer.reset-size3.size5,.katex .sizing.reset-size3.size5{font-size:1.28571429em}.katex .fontsize-ensurer.reset-size3.size6,.katex .sizing.reset-size3.size6{font-size:1.42857143em}.katex .fontsize-ensurer.reset-size3.size7,.katex .sizing.reset-size3.size7{font-size:1.71428571em}.katex .fontsize-ensurer.reset-size3.size8,.katex .sizing.reset-size3.size8{font-size:2.05714286em}.katex .fontsize-ensurer.reset-size3.size9,.katex .sizing.reset-size3.size9{font-size:2.46857143em}.katex .fontsize-ensurer.reset-size3.size10,.katex .sizing.reset-size3.size10{font-size:2.96285714em}.katex .fontsize-ensurer.reset-size3.size11,.katex .sizing.reset-size3.size11{font-size:3.55428571em}.katex .fontsize-ensurer.reset-size4.size1,.katex .sizing.reset-size4.size1{font-size:.625em}.katex .fontsize-ensurer.reset-size4.size2,.katex .sizing.reset-size4.size2{font-size:.75em}.katex .fontsize-ensurer.reset-size4.size3,.katex .sizing.reset-size4.size3{font-size:.875em}.katex .fontsize-ensurer.reset-size4.size4,.katex .sizing.reset-size4.size4{font-size:1em}.katex .fontsize-ensurer.reset-size4.size5,.katex .sizing.reset-size4.size5{font-size:1.125em}.katex .fontsize-ensurer.reset-size4.size6,.katex .sizing.reset-size4.size6{font-size:1.25em}.katex .fontsize-ensurer.reset-size4.size7,.katex .sizing.reset-size4.size7{font-size:1.5em}.katex .fontsize-ensurer.reset-size4.size8,.katex .sizing.reset-size4.size8{font-size:1.8em}.katex .fontsize-ensurer.reset-size4.size9,.katex .sizing.reset-size4.size9{font-size:2.16em}.katex .fontsize-ensurer.reset-size4.size10,.katex .sizing.reset-size4.size10{font-size:2.5925em}.katex .fontsize-ensurer.reset-size4.size11,.katex .sizing.reset-size4.size11{font-size:3.11em}.katex .fontsize-ensurer.reset-size5.size1,.katex .sizing.reset-size5.size1{font-size:.55555556em}.katex .fontsize-ensurer.reset-size5.size2,.katex .sizing.reset-size5.size2{font-size:.66666667em}.katex .fontsize-ensurer.reset-size5.size3,.katex .sizing.reset-size5.size3{font-size:.77777778em}.katex .fontsize-ensurer.reset-size5.size4,.katex .sizing.reset-size5.size4{font-size:.88888889em}.katex .fontsize-ensurer.reset-size5.size5,.katex .sizing.reset-size5.size5{font-size:1em}.katex .fontsize-ensurer.reset-size5.size6,.katex .sizing.reset-size5.size6{font-size:1.11111111em}.katex .fontsize-ensurer.reset-size5.size7,.katex .sizing.reset-size5.size7{font-size:1.33333333em}.katex .fontsize-ensurer.reset-size5.size8,.katex .sizing.reset-size5.size8{font-size:1.6em}.katex .fontsize-ensurer.reset-size5.size9,.katex .sizing.reset-size5.size9{font-size:1.92em}.katex .fontsize-ensurer.reset-size5.size10,.katex .sizing.reset-size5.size10{font-size:2.30444444em}.katex .fontsize-ensurer.reset-size5.size11,.katex .sizing.reset-size5.size11{font-size:2.76444444em}.katex .fontsize-ensurer.reset-size6.size1,.katex .sizing.reset-size6.size1{font-size:.5em}.katex .fontsize-ensurer.reset-size6.size2,.katex .sizing.reset-size6.size2{font-size:.6em}.katex .fontsize-ensurer.reset-size6.size3,.katex .sizing.reset-size6.size3{font-size:.7em}.katex .fontsize-ensurer.reset-size6.size4,.katex .sizing.reset-size6.size4{font-size:.8em}.katex .fontsize-ensurer.reset-size6.size5,.katex .sizing.reset-size6.size5{font-size:.9em}.katex .fontsize-ensurer.reset-size6.size6,.katex .sizing.reset-size6.size6{font-size:1em}.katex .fontsize-ensurer.reset-size6.size7,.katex .sizing.reset-size6.size7{font-size:1.2em}.katex .fontsize-ensurer.reset-size6.size8,.katex .sizing.reset-size6.size8{font-size:1.44em}.katex .fontsize-ensurer.reset-size6.size9,.katex .sizing.reset-size6.size9{font-size:1.728em}.katex .fontsize-ensurer.reset-size6.size10,.katex .sizing.reset-size6.size10{font-size:2.074em}.katex .fontsize-ensurer.reset-size6.size11,.katex .sizing.reset-size6.size11{font-size:2.488em}.katex .fontsize-ensurer.reset-size7.size1,.katex .sizing.reset-size7.size1{font-size:.41666667em}.katex .fontsize-ensurer.reset-size7.size2,.katex .sizing.reset-size7.size2{font-size:.5em}.katex .fontsize-ensurer.reset-size7.size3,.katex .sizing.reset-size7.size3{font-size:.58333333em}.katex .fontsize-ensurer.reset-size7.size4,.katex .sizing.reset-size7.size4{font-size:.66666667em}.katex .fontsize-ensurer.reset-size7.size5,.katex .sizing.reset-size7.size5{font-size:.75em}.katex .fontsize-ensurer.reset-size7.size6,.katex .sizing.reset-size7.size6{font-size:.83333333em}.katex .fontsize-ensurer.reset-size7.size7,.katex .sizing.reset-size7.size7{font-size:1em}.katex .fontsize-ensurer.reset-size7.size8,.katex .sizing.reset-size7.size8{font-size:1.2em}.katex .fontsize-ensurer.reset-size7.size9,.katex .sizing.reset-size7.size9{font-size:1.44em}.katex .fontsize-ensurer.reset-size7.size10,.katex .sizing.reset-size7.size10{font-size:1.72833333em}.katex .fontsize-ensurer.reset-size7.size11,.katex .sizing.reset-size7.size11{font-size:2.07333333em}.katex .fontsize-ensurer.reset-size8.size1,.katex .sizing.reset-size8.size1{font-size:.34722222em}.katex .fontsize-ensurer.reset-size8.size2,.katex .sizing.reset-size8.size2{font-size:.41666667em}.katex .fontsize-ensurer.reset-size8.size3,.katex .sizing.reset-size8.size3{font-size:.48611111em}.katex .fontsize-ensurer.reset-size8.size4,.katex .sizing.reset-size8.size4{font-size:.55555556em}.katex .fontsize-ensurer.reset-size8.size5,.katex .sizing.reset-size8.size5{font-size:.625em}.katex .fontsize-ensurer.reset-size8.size6,.katex .sizing.reset-size8.size6{font-size:.69444444em}.katex .fontsize-ensurer.reset-size8.size7,.katex .sizing.reset-size8.size7{font-size:.83333333em}.katex .fontsize-ensurer.reset-size8.size8,.katex .sizing.reset-size8.size8{font-size:1em}.katex .fontsize-ensurer.reset-size8.size9,.katex .sizing.reset-size8.size9{font-size:1.2em}.katex .fontsize-ensurer.reset-size8.size10,.katex .sizing.reset-size8.size10{font-size:1.44027778em}.katex .fontsize-ensurer.reset-size8.size11,.katex .sizing.reset-size8.size11{font-size:1.72777778em}.katex .fontsize-ensurer.reset-size9.size1,.katex .sizing.reset-size9.size1{font-size:.28935185em}.katex .fontsize-ensurer.reset-size9.size2,.katex .sizing.reset-size9.size2{font-size:.34722222em}.katex .fontsize-ensurer.reset-size9.size3,.katex .sizing.reset-size9.size3{font-size:.40509259em}.katex .fontsize-ensurer.reset-size9.size4,.katex .sizing.reset-size9.size4{font-size:.46296296em}.katex .fontsize-ensurer.reset-size9.size5,.katex .sizing.reset-size9.size5{font-size:.52083333em}.katex .fontsize-ensurer.reset-size9.size6,.katex .sizing.reset-size9.size6{font-size:.5787037em}.katex .fontsize-ensurer.reset-size9.size7,.katex .sizing.reset-size9.size7{font-size:.69444444em}.katex .fontsize-ensurer.reset-size9.size8,.katex .sizing.reset-size9.size8{font-size:.83333333em}.katex .fontsize-ensurer.reset-size9.size9,.katex .sizing.reset-size9.size9{font-size:1em}.katex .fontsize-ensurer.reset-size9.size10,.katex .sizing.reset-size9.size10{font-size:1.20023148em}.katex .fontsize-ensurer.reset-size9.size11,.katex .sizing.reset-size9.size11{font-size:1.43981481em}.katex .fontsize-ensurer.reset-size10.size1,.katex .sizing.reset-size10.size1{font-size:.24108004em}.katex .fontsize-ensurer.reset-size10.size2,.katex .sizing.reset-size10.size2{font-size:.28929605em}.katex .fontsize-ensurer.reset-size10.size3,.katex .sizing.reset-size10.size3{font-size:.33751205em}.katex .fontsize-ensurer.reset-size10.size4,.katex .sizing.reset-size10.size4{font-size:.38572806em}.katex .fontsize-ensurer.reset-size10.size5,.katex .sizing.reset-size10.size5{font-size:.43394407em}.katex .fontsize-ensurer.reset-size10.size6,.katex .sizing.reset-size10.size6{font-size:.48216008em}.katex .fontsize-ensurer.reset-size10.size7,.katex .sizing.reset-size10.size7{font-size:.57859209em}.katex .fontsize-ensurer.reset-size10.size8,.katex .sizing.reset-size10.size8{font-size:.69431051em}.katex .fontsize-ensurer.reset-size10.size9,.katex .sizing.reset-size10.size9{font-size:.83317261em}.katex .fontsize-ensurer.reset-size10.size10,.katex .sizing.reset-size10.size10{font-size:1em}.katex .fontsize-ensurer.reset-size10.size11,.katex .sizing.reset-size10.size11{font-size:1.19961427em}.katex .fontsize-ensurer.reset-size11.size1,.katex .sizing.reset-size11.size1{font-size:.20096463em}.katex .fontsize-ensurer.reset-size11.size2,.katex .sizing.reset-size11.size2{font-size:.24115756em}.katex .fontsize-ensurer.reset-size11.size3,.katex .sizing.reset-size11.size3{font-size:.28135048em}.katex .fontsize-ensurer.reset-size11.size4,.katex .sizing.reset-size11.size4{font-size:.32154341em}.katex .fontsize-ensurer.reset-size11.size5,.katex .sizing.reset-size11.size5{font-size:.36173633em}.katex .fontsize-ensurer.reset-size11.size6,.katex .sizing.reset-size11.size6{font-size:.40192926em}.katex .fontsize-ensurer.reset-size11.size7,.katex .sizing.reset-size11.size7{font-size:.48231511em}.katex .fontsize-ensurer.reset-size11.size8,.katex .sizing.reset-size11.size8{font-size:.57877814em}.katex .fontsize-ensurer.reset-size11.size9,.katex .sizing.reset-size11.size9{font-size:.69453376em}.katex .fontsize-ensurer.reset-size11.size10,.katex .sizing.reset-size11.size10{font-size:.83360129em}.katex .fontsize-ensurer.reset-size11.size11,.katex .sizing.reset-size11.size11{font-size:1em}.katex .delimsizing.size1{font-family:KaTeX_Size1}.katex .delimsizing.size2{font-family:KaTeX_Size2}.katex .delimsizing.size3{font-family:KaTeX_Size3}.katex .delimsizing.size4{font-family:KaTeX_Size4}.katex .delimsizing.mult .delim-size1>span{font-family:KaTeX_Size1}.katex .delimsizing.mult .delim-size4>span{font-family:KaTeX_Size4}.katex .nulldelimiter{display:inline-block;width:.12em}.katex .delimcenter,.katex .op-symbol{position:relative}.katex .op-symbol.small-op{font-family:KaTeX_Size1}.katex .op-symbol.large-op{font-family:KaTeX_Size2}.katex .accent>.vlist-t,.katex .op-limits>.vlist-t{text-align:center}.katex .accent .accent-body{position:relative}.katex .accent .accent-body:not(.accent-full){width:0}.katex .overlay{display:block}.katex .mtable .vertical-separator{display:inline-block;min-width:1px}.katex .mtable .arraycolsep{display:inline-block}.katex .mtable .col-align-c>.vlist-t{text-align:center}.katex .mtable .col-align-l>.vlist-t{text-align:left}.katex .mtable .col-align-r>.vlist-t{text-align:right}.katex .svg-align{text-align:left}.katex svg{fill:currentColor;stroke:currentColor;fill-rule:nonzero;fill-opacity:1;stroke-width:1;stroke-linecap:butt;stroke-linejoin:miter;stroke-miterlimit:4;stroke-dasharray:none;stroke-dashoffset:0;stroke-opacity:1;display:block;height:inherit;position:absolute;width:100%}.katex svg path{stroke:none}.katex img{border-style:none;max-height:none;max-width:none;min-height:0;min-width:0}.katex .stretchy{display:block;overflow:hidden;position:relative;width:100%}.katex .stretchy:after,.katex .stretchy:before{content:""}.katex .hide-tail{overflow:hidden;position:relative;width:100%}.katex .halfarrow-left{left:0;overflow:hidden;position:absolute;width:50.2%}.katex .halfarrow-right{overflow:hidden;position:absolute;right:0;width:50.2%}.katex .brace-left{left:0;overflow:hidden;position:absolute;width:25.1%}.katex .brace-center{left:25%;overflow:hidden;position:absolute;width:50%}.katex .brace-right{overflow:hidden;position:absolute;right:0;width:25.1%}.katex .x-arrow-pad{padding:0 .5em}.katex .cd-arrow-pad{padding:0 .55556em 0 .27778em}.katex .mover,.katex .munder,.katex .x-arrow{text-align:center}.katex .boxpad{padding:0 .3em}.katex .fbox,.katex .fcolorbox{border:.04em solid;box-sizing:border-box}.katex .cancel-pad{padding:0 .2em}.katex .cancel-lap{margin-left:-.2em;margin-right:-.2em}.katex .sout{border-bottom-style:solid;border-bottom-width:.08em}.katex .angl{border-right:.049em solid;border-top:.049em solid;box-sizing:border-box;margin-right:.03889em}.katex .anglpad{padding:0 .03889em}.katex .eqn-num:before{content:"(" counter(katexEqnNo) ")";counter-increment:katexEqnNo}.katex .mml-eqn-num:before{content:"(" counter(mmlEqnNo) ")";counter-increment:mmlEqnNo}.katex .mtr-glue{width:50%}.katex .cd-vert-arrow{display:inline-block;position:relative}.katex .cd-label-left{display:inline-block;position:absolute;right:calc(50% + .3em);text-align:left}.katex .cd-label-right{display:inline-block;left:calc(50% + .3em);position:absolute;text-align:right}.katex-display{display:block;margin:1em 0;text-align:center}.katex-display>.katex{display:block;text-align:center;white-space:nowrap}.katex-display>.katex>.katex-html{display:block;position:relative}.katex-display>.katex>.katex-html>.tag{position:absolute;right:0}.katex-display.leqno>.katex>.katex-html>.tag{left:0;right:auto}.katex-display.fleqn>.katex{padding-left:2em;text-align:left}body{counter-reset:katexEqnNo mmlEqnNo}
`


export async function mdToHtml(markdown) {
  const file = await unified()
        .use(remarkParse)
        .use(remarkGFM)
        .use(remarkBreaks)
        .use(remarkMath, {singleDollarTextMath: true})
        .use(remarkRehype)
        .use(rehypeRaw)
        .use(rehypeKatex)
        .use(rehypeStringify, { allowDangerousHtml: true })
        .process(markdown);
  const html = String(file)
  const result = `<html><style>${katexCss}</style><body>${html}</html>`
  return result
}

export async function mdToHtml1(markdown, { latex = true } = {}) {
  const processor = unified()
    .use(remarkParse)
    .use(remarkGFM)
    .use(remarkBreaks);

    processor.use(remarkMath, { singleDollarTextMath: true });


  const file = await processor
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(latex ? rehypeKatex : () => {}, { throwOnError: false, strict: "ignore" })
    .use(rehypeStringify, { allowDangerousHtml: true })
    .process(markdown);

  
  const html = String(file);
  console.log("contains katex html?", html.includes('class="katex"'));
  console.log("still contains $$?", html.includes("$$"));
  return html
}

/**
 * Inserts newlines before and after specified XML tags in a Markdown string.
 *
 * @param {string} markdown - The Markdown content.
 * @param {string[]} tags - Array of XML tag names to target.
 * @returns {string} - The updated Markdown with newlines added around the XML tags.
 */
function addNewlinesToXmlTags(markdown, tags) {
  tags.forEach(tag => {
    // Create regex patterns for:
    // 2. Opening tags: <tag ...>
    // 3. Closing tags: </tag>
    // Pattern for opening tags (non self-closing)
    const openingRegex = new RegExp(`<${tag}(\\s[^>]*?)?>`, 'g');
    markdown = markdown.replace(openingRegex, (match) => {
      return `\n${match.trim()}\n`;
    });

    // Pattern for closing tags
    const closingRegex = new RegExp(`</${tag}>`, 'g');
    markdown = markdown.replace(closingRegex, (match) => {
      return `\n\n${match.trim()}\n\n`;
    });
  });
  return markdown;
}

const validateMarkdown = (markdown) => {
  const errors = [];
  
  // Check for unmatched backticks
  const backtickCount = (markdown.match(/`/g) || []).length;
  if (backtickCount % 2 !== 0) {
    errors.push('Unmatched backticks (`) detected');
  }

  // Check for unmatched code block fences
  const codeBlockStarts = (markdown.match(/^```\w*$/gm) || []).length;
  const codeBlockEnds = (markdown.match(/^```$/gm) || []).length;
  if (codeBlockStarts !== codeBlockEnds) {
    errors.push('Unmatched code block fences (```) detected');
  }


  // Check for unmatched emphasis markers (* and _)
  const asteriskCount = (markdown.match(/\*/g) || []).length;
  const underscoreCount = (markdown.match(/_/g) || []).length;
  if (asteriskCount % 2 !== 0) {
    errors.push('Unmatched asterisks (*) detected');
  }
  if (underscoreCount % 2 !== 0) {
    errors.push('Unmatched underscores (_) detected');
  }

  // Check for malformed links
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const links = [...markdown.matchAll(linkPattern)];
  links.forEach(link => {
    const [fullMatch, text, url] = link;
    if (!text.trim()) {
      errors.push(`Empty link text in: ${fullMatch}`);
    }
    if (!url.trim()) {
      errors.push(`Empty URL in: ${fullMatch}`);
    }
  });

  // Check for broken reference-style links
  const references = new Set();
  const referenceDefinitions = markdown.match(/^\[.+\]:\s+\S+$/gm) || [];
  referenceDefinitions.forEach(def => {
    const reference = def.match(/^\[(.+)\]/)[1];
    references.add(reference);
  });

  const referenceLinks = markdown.match(/\[.+\]\[.+\]/g) || [];
  referenceLinks.forEach(link => {
    const reference = link.match(/\[.+\]\[(.+)\]/)[1];
    if (!references.has(reference)) {
      errors.push(`Missing reference definition for: ${reference}`);
    }
  });

  // Check for malformed lists
  const listLines = markdown.match(/^[\s-*+]\s.+/gm) || [];
  listLines.forEach(line => {
    if (!line.match(/^[\s-*+]\s+\S/)) {
      errors.push(`Malformed list item: "${line}" (missing space after list marker)`);
    }
  });

  return errors;
};


/**
 * Determines whether a text is likely to contain LaTeX mathematical notation.
 * Handles multiple currency values while avoiding false positives.
 * 
 * @param {string} text - The input text to analyze
 * @returns {boolean} - True if the text likely contains LaTeX math, false otherwise
 */
function isLaTeXText(text) {
    // No dollar signs means no LaTeX math
    if (!text.includes('$')) {
        return false;
    }


  if (text.includes('$$')) {
    return true
  }



    // First, let's look for clear currency patterns that would rule out LaTeX
    const currencyPatterns = [
        // Multiple currency amounts in sequence indicate a price list
        /(?:\$\s*[\d,]+\.?\d*\s*(?:,|\s+|and\s+|&\s+|\$|$))+/,
        
        // Currency with units or labels
        /\$\s*[\d,]+\.?\d*\s*(?:USD|dollars?|k|K|thousand|million|billion)/i,
        
        // Price/cost labels
        /(?:price|cost|fee|payment|paid|amount)s?:?\s*\$\s*[\d,]+\.?\d*/i,
        
        // Lists and ranges of prices
        /\$\s*[\d,]+\.?\d*\s*(?:-|to|or)\s*\$\s*[\d,]+\.?\d*/,
        
        // Simple dollar amounts with optional decimals and commas
        /^\s*\$\s*[\d,]+\.?\d*\s*$/
    ];

    // If any currency pattern matches strongly, it's probably not LaTeX
    for (const pattern of currencyPatterns) {
        if (pattern.test(text)) {
            return false;
        }
    }

    // Now look for LaTeX indicators
    const latexPatterns = [
        // LaTeX commands
        /\$[^$]*\\[a-zA-Z]+/,
        
        // Mathematical notation
        /\$[^$]*[_^{}\[\]\\].*[_^{}\[\]\\][^$]*\$/,
        
        // Display math mode
        /\$\$[\s\S]+\$\$/,
        
        // Greek letters and common math operators
        /\$[^$]*\\(?:alpha|beta|gamma|theta|sum|int|frac|sqrt)/,
        
        // Variables with subscripts/superscripts
        /\$[^$]*[a-zA-Z]_\{[^}]+\}/,
        
        // Matrix notation
        /\$[^$]*\\begin\{[^}]+\}/
    ];

    // Count the number of LaTeX patterns we find
    const latexMatches = latexPatterns.reduce((count, pattern) => 
        count + (pattern.test(text) ? 1 : 0), 0);

    // Additional checks for LaTeX characteristics
    const hasMathKeywords = /\\(?:begin|end|text|left|right|quad|qquad)/.test(text);
    const hasComplexMath = /[_^]{\w+}/.test(text);

    // Make final determination
    // We need clear evidence of LaTeX patterns and no strong currency indicators
    return latexMatches > 0 || hasMathKeywords || hasComplexMath;
}

// Example usage with test cases
function runTests() {
    const testCases = [
        {
            text: "$100, $200, $300",
            expectedResult: false,
            description: "List of prices"
        },
        {
            text: "Prices: $50-$100",
            expectedResult: false,
            description: "Price range"
        },
        {
            text: "The integral is $\\int_0^\\infty e^{-x} dx$",
            expectedResult: true,
            description: "Mathematical integral"
        },
        {
            text: "Let $\\alpha$ be the angle",
            expectedResult: true,
            description: "Greek letter"
        },
        {
            text: "$1000 per month",
            expectedResult: false,
            description: "Simple currency amount"
        },
        {
            text: "Total: $500k in revenue",
            expectedResult: false,
            description: "Currency with k suffix"
        },
        {
            text: "$x^2 + y^2 = r^2$",
            expectedResult: true,
            description: "Circle equation"
        },
        {
            text: "Costs were $100 and $200",
            expectedResult: false,
            description: "Multiple prices in sentence"
        }
    ];

    //console.log("Running test cases:");
    testCases.forEach((test, index) => {
        const result = isLaTeXText(test.text);
        //console.log(`\nTest ${index + 1}: ${test.description}`);
        //console.log(`Input: ${test.text}`);
        //console.log(`Expected: ${test.expectedResult}, Got: ${result}`);
        //console.log(`Result: ${result === test.expectedResult ? 'PASS' : 'FAIL'}`);
    });
  //debugger
}


runTests()

function convertBracketsToDollars(text) {
  return text.replaceAll('\\[', '$$$$').replaceAll('\\]', '$$$$')
}


export const containsLatex = (text) => {
  text = preprocessLaTeX(text)
  const latexPatterns = [/mathbb/, /\$\\triangle/,/\$\$[^]+\$\$/, /$[^0-9]/, /\$[^$]*\S\$/, /[$][\\]/,/\\begin{.*}.*\\end{.*}/, /\\(frac|log_|text|times|textit|textbf){/, /\\times/, /\\(to|boxed)/, /\\cup/, /\\(le|ge)/, /\\\(|\\\)/, /[\$[^ ]+[{$]/];
  let result = latexPatterns.find((pattern, i) => {
    const matched = pattern.test(text)
    if (matched && i === 3 ) {
      const segments = classifySegments(text)
      //////console.log('segments', JSON.stringify(segments))
      for (const segment of segments) {
        const { token, type } = segment
        if (token.indexOf('$') >= 0) {
          if (type !== 'latex') {
            return false
          }
        }
      }
      //////console.log('matched', pattern, matched, text)
    }
    return matched
  }) ? text : undefined
  if (result) {
    //////console.log('containsLatext', result, text)
  }
  return result
}

// Function to classify segments of text
function classifySegments(text) {
    const monetaryPattern = /\$\d+/g;
    const latexPattern = /\$[^\$]+\$/g;

    // Find all matches for monetary amounts and LaTeX expressions
    const monetaryMatches = text.match(monetaryPattern) || [];
    const latexMatches = text.match(latexPattern) || [];

    // Classify text based on matches
    const classifiedText = text.split(' ').map(token => {
        if (monetaryMatches.includes(token)) {
            return { token, type: 'monetary' };
        } else if (latexMatches.some(expr => expr.includes(token))) {
            return { token, type: 'latex' };
        } else {
            return { token, type: 'text' };
        }
    });

    return classifiedText;
}

export const containsMarkdown = (text) => {
  const markdownPatterns = [/^#+\s/, /^[*-]\s/, /\[.*\]\(.*\)/, /[*][*]/, /##/, /[*][^*]+[*]/, /```/];
  return markdownPatterns.some(pattern => {
    const result = pattern.test(text)
    if (result) {
      ////console.log("matched", pattern, text)
    }
    return result
  });
}

export class Markdown extends React.Component {

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidUpdate() {
  }

  componentDidMount() {
    //console.log("markdown mounted", this.props.children)
  }

  componentWillUnmount() {
    //console.log("markdown mounted", this.props.children)
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    //console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  cachedText = ''
  cached = null

  render() {
    let text = (""+this.props.children).trim()
    if (text == this.cachedText && this.cached) {
      return this.cached
    }
    this.cachedText = text
    function convertSingleToDoubleNewlines(text) {
      return text.replace(/([^\n])\n(?!\n)/g, '$1\n\n');
    }
    let modified = addNewlinesToXmlTags(text, ["think", "signature", "reasoning", "thinking"])
    if (modified !== text) {
      console.log("ADDED newlines", modified)
      text = modified
    }
    text = preprocessLaTeX(text)
    //text = convertSingleToDoubleNewlines(text)
    //////console.log("markdown not cached", text)
    let katex
    let latex
    latex = isLaTeXText(text)
    let plugins = [remarkGFM, remarkBreaks]
    let rehypePlugins = [rehypeRaw]
    if (latex) {
      rehypePlugins.push(katex = rehypeKatex)
      plugins = plugins.concat([[remarkMath, { singleDollarTextMath: true}]])
    } else {
      if (text.indexOf("$") >= 0) {
        //////console.log("!containsLatex", text)
      }
    }
    if (!katex && !containsMarkdown(text)) {
      //return text
    }
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return text
    }
    if (this.props.debug) {
      //console.log('re-rendering markdown', text)
    }

    const copy = (e) => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) return;
      e.preventDefault();
      // Plain text
      const plainText = selection.toString();

      // HTML
      const fragment = range.cloneContents();
      const div = document.createElement("div");
      div.appendChild(fragment);
      const html = div.innerHTML;
      e.clipboardData.setData("text/plain", plainText);
      e.clipboardData.setData("text/html", html);
    }
    
    return this.cached = <div className='markdownContainer' >
                           <ReactMarkdown key={this.props.key1}
                                          remarkPlugins={plugins}
                                          rehypePlugins={rehypePlugins}
                                          urlTransform={attunewiseUrlTransform}
                                          components={this.props.components}>
                             {text}
                           </ReactMarkdown>
                         </div>
  }
}



 




 
